import { create } from 'zustand';
import i18n from '@/i18n';
import { api } from '@/lib/api/client';

interface ScoutProfile {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  source: string;
  level: number;
  currentXp: number;
  totalXp: number;
  currentStreak: number;
  longestStreak: number;
  totalScans: number;
  totalFeedback: number;
  totalIssues: number;
  referralCode: string;
  totalReferrals: number;
  totalCompanyReferrals: number;
  trustScore: number;
  globeEnabled: boolean;
  createdAt: string;
}

interface AuthState {
  scout: ScoutProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  magicLinkSent: boolean;

  requestMagicLink: (email: string, referralCode?: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  resetMagicLinkSent: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  scout: null,
  isAuthenticated: !!localStorage.getItem('scout_accessToken'),
  isLoading: false,
  error: null,
  magicLinkSent: false,

  requestMagicLink: async (email: string, referralCode?: string) => {
    set({ isLoading: true, error: null, magicLinkSent: false });
    try {
      // UI language: the login email is written in it (and saved on a new Scout)
      const body: Record<string, string> = { email, language: i18n.language };
      if (referralCode) body.referralCode = referralCode;
      await api.post('/scout/auth/request-link', body);
      set({ magicLinkSent: true, isLoading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : i18n.t('errors:store.failedToSendMagicLink'),
        isLoading: false,
      });
    }
  },

  verifyMagicLink: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await api.post<{ scout: ScoutProfile; tokens: { accessToken: string; refreshToken: string } }>(
        '/scout/auth/verify',
        { token, language: i18n.language },
      );
      api.setAccessToken(result.tokens.accessToken);
      localStorage.setItem('scout_refreshToken', result.tokens.refreshToken);
      localStorage.setItem('scout_email', result.scout.email);
      set({ scout: result.scout, isAuthenticated: true, isLoading: false });
      window.gtag?.('event', 'scout_login');
      window.gtag?.('set', 'user_properties', {
        globe_enabled: result.scout.globeEnabled ? 'true' : 'false',
        ranger_level: String(result.scout.level),
      });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : i18n.t('errors:store.invalidOrExpiredLink'),
        isLoading: false,
      });
    }
  },

  logout: () => {
    api.setAccessToken(null);
    localStorage.removeItem('scout_refreshToken');
    set({ scout: null, isAuthenticated: false, error: null });
  },

  checkAuth: async () => {
    if (!localStorage.getItem('scout_accessToken')) {
      set({ isAuthenticated: false, scout: null });
      return;
    }
    set({ isLoading: true });
    try {
      const scout = await api.get<ScoutProfile>('/scout/auth/me');
      set({ scout, isAuthenticated: true, isLoading: false });
      window.gtag?.('set', 'user_properties', {
        globe_enabled: scout.globeEnabled ? 'true' : 'false',
        ranger_level: String(scout.level),
      });
    } catch {
      api.setAccessToken(null);
      localStorage.removeItem('scout_refreshToken');
      set({ scout: null, isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
  resetMagicLinkSent: () => set({ magicLinkSent: false }),
}));
